import { Formik, getIn } from "formik";
import { useContext, useState } from "react";
import Select from "react-select";
import * as yup from "yup";

import DatePicker from "react-date-picker";
import appStyles from "../../../App.module.css";
import { selectControlStyling, selectOptionControlStyling } from "../../../amt/config/amt-constants";
import appConstants, { convertDateToApiFormat } from "../../../app-constants";
import ScrollToFirstError from "../../../common-components/ScrollToFirstError";
import { PortuguesePurchasePathContext } from "../../PortuguesePurchasePath";
import PortugueseQuoteApi from "../../api/PortugueseQuoteApi";
import { IPortugueseInitialQuoteData, IPortuguesePurchasePathContext } from "../../config/interface";
import { calculateAge, portugueseUrlAndDocs, productBasedDestinationCountry } from "../../config/portuguese-Constant";
import NextButton from "../buttons/NextButton";
import PrevButton from "../buttons/PrevButton";
import PPFooter from "../common/PPFooter";
import PPLoadingIndicator from "../common/PPLoadingIndicator";
import PPMobileFooter from "../common/PPMobileFooter";
import PPQuoteSummary from "../common/PPQuoteSummary";
import PPShowApiError from "../common/PPShowApiError";
import PPStepper from "../common/PPStepper";
import PPTripSummary from "../common/PPTripSummary";
import PPShowFormikError from "../page/PPShowFormikError";

const PPOrderForm = () => {
  const { quoteData, setQuoteData, previous, quoteResult, setOrder, next, hostCountries, loader } = useContext(
    PortuguesePurchasePathContext
  ) as IPortuguesePurchasePathContext;

  const [loading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<any | null>(quoteData.host_country);

  const validationSchema = yup.object().shape({
    applicants: yup.object().shape({
      first_name: yup.string().trim().required(),
      middle_name: yup.string().trim(),
      last_name: yup.string().trim().required(),
      dob: yup.date().required(),
      is_main: yup.boolean(),
      email: yup.string().email().matches(appConstants.EMAIL_REGEX).required(),
      contact_number: yup.string().required(),
      address: yup.string().required(),
      city: yup.string().required(),
      post_code: yup.string().required(),
    }),
    accept_terms_conditions: yup.boolean().required().oneOf([true], "You must accept the terms and conditions."),
    consent_personal_data: yup.boolean().required().oneOf([true], "You must confirm the statement."),

    on_behalf_anyone_else: yup.boolean().required().oneOf([true], "You must confirm the statement."),
    acknowledge: yup.boolean().required().oneOf([true], "You must confirm the statement."),
    coverage_outside_residence_policy: yup.boolean().required().oneOf([true], "You must confirm the statement."),
    insurance_disclosure: yup.boolean().required().oneOf([true], "You must confirm the statement."),
  });

  const initApplicant = {
    first_name: quoteData?.applicantData?.first_name ?? "",
    middle_name: quoteData?.applicantData?.middle_name ?? "",
    last_name: quoteData?.applicantData?.last_name ?? "",
    dob: quoteData?.main_applicant,
    age_at_travel: calculateAge(quoteData.main_applicant).years,
    email: quoteData?.applicantData?.email ?? "",
    contact_number: quoteData?.applicantData?.contact_number ?? "",
    address: quoteData?.applicantData?.address ?? "",
    city: quoteData?.applicantData?.city ?? "",
    post_code: quoteData?.applicantData?.post_code ?? "",
    country_code: quoteData?.applicantData?.country_code ?? quoteData?.host_country?.code,
    is_main: true,
    trip_cost: 1000,
  };

  const initialFormValues: any = {
    applicants: initApplicant,
    accept_terms_conditions: false,
    consent_personal_data: false,
    on_behalf_anyone_else: false,
    acknowledge: false,
    coverage_outside_residence_policy: false,
    insurance_disclosure: false,
  };

  const getApplicantErrorClass = (errors: any, touched: any, name: string) => {
    if (errors?.applicants?.[name] && touched?.applicants?.[name]) {
      return appStyles.hasError;
    }
    return "";
  };

  const handleFormSubmit = async (values: any) => {
    try {
      setLoading(true);
      setHasError(null);

      const _quoteData: IPortugueseInitialQuoteData = { ...quoteData };
      _quoteData.applicantData = values.applicants;
      setQuoteData(_quoteData);

      const details: any[] = [];
      if (quoteResult?.deductibles?.excess_fee) {
        details.push({
          type: "deductible",
          detail: "excess_fee",
          amount: quoteResult?.deductibles?.excess_fee,
        });
      }

      quoteResult?.details.forEach((detail: any) => {
        details.push({
          type: "package",
          product_package_id: detail.package_alias,
        });
      });

      let _applicants: any = {
        first_name: values.applicants.first_name,
        last_name: values.applicants.last_name,
        dob: convertDateToApiFormat(values.applicants.dob),
        age_at_travel: values.applicants.age_at_travel,
        email: values.applicants.email,
        contact_number: values.applicants.contact_number,
        address: values.applicants.address,
        post_code: values.applicants.post_code,
        city: values.applicants.city,
        country_code: values.applicants.country_code,
        is_main: true,
        trip_cost: 1000,
      };

      if (values.applicants.middle_name.trim().length !== 0) {
        _applicants.middle_name = values.applicants.middle_name;
      }

      if (_quoteData?.host_country?.states && _quoteData?.host_country?.states?.length > 0) {
        _applicants.state_code = _quoteData.host_country?.states[0]?.code;
      }

      const data: any = {
        quote_id: quoteResult?.id,
        currency_id: quoteData.currency?.value,
        sanction_search_id: 0,
        product_id: quoteData.productId,
        start_at: convertDateToApiFormat(quoteData.start_date),
        end_at: convertDateToApiFormat(quoteData.end_date),
        signed_at: convertDateToApiFormat(new Date()),
        deposit_at: convertDateToApiFormat(new Date()),
        details: details,
        applicants: [_applicants],
        destinations: [
          {
            destination_group: productBasedDestinationCountry[quoteData.productId],
          },
        ],
        affiliate: {
          id: quoteData.affiliate_id,
        },
      };

      const response: any = await PortugueseQuoteApi.createOrder(data);

      if (response.error) {
        throw response;
      }

      setOrder(response.data);
      next();
    } catch (e: any) {
      console.log(e);
      setHasError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <PPLoadingIndicator />}
      <Formik initialValues={initialFormValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
        {({
          values,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          handleSubmit,
          handleChange,
          isValid,
          submitCount,
          validateForm,
        }) => (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              await validateForm();
              handleSubmit();
            }}
          >
            <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
            <div className={`${appStyles.mainContainer}`}>
              <div className={`${appStyles.quoteResultWrapper} ${appStyles.PPQuoteWrapper}`}>
                <div className={`${appStyles.quoteDetailsBox}`}>
                  <div className={appStyles.headerWrapper}>
                    <div className={appStyles.pageTitle}>
                      <h2 className={`${appStyles.colorPrimary}`}>Policy Information</h2>
                    </div>
                  </div>
                  <PPStepper />
                  <PPTripSummary />

                  <>
                    <div className={appStyles.pageSubTitle}>
                      <h3 className={appStyles.colorPrimary}>Primary Traveller Information (You)</h3>
                    </div>

                    <div className={`${appStyles.quoteFormBlock} ${appStyles.PPQuoteFormBlock}`}>
                      <div className={appStyles.quoteFormBlockTitle}>
                        <h4 className={appStyles.colorPrimary}>Personal Information</h4>
                      </div>

                      <div className={appStyles.formRow}>
                        <div className={appStyles.formField}>
                          <label>Date of birth:</label>
                        </div>
                        <div className={appStyles.formField}>
                          <DatePicker
                            className={appStyles.bfpDatePicker}
                            format={appConstants.datePickerFormat}
                            value={quoteData?.main_applicant}
                            calendarIcon={() => null}
                            clearIcon={() => null}
                            disabled={true}
                          />
                        </div>
                      </div>

                      <div className={appStyles.formRow}>
                        <label>Your full name:</label>
                      </div>
                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${getApplicantErrorClass(
                              errors,
                              touched,
                              "first_name"
                            )}`}
                          >
                            <input
                              type="text"
                              name={`applicants.first_name`}
                              id={`applicants.first_name`}
                              value={values.applicants?.first_name}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                              placeholder="First Name*"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <PPShowFormikError name="applicants.first_name" />
                          </div>
                        </div>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${getApplicantErrorClass(
                              errors,
                              touched,
                              "middle_name"
                            )}`}
                          >
                            <input
                              type="text"
                              name={`applicants.middle_name`}
                              id={`applicants.middle_name`}
                              value={values.applicants?.middle_name}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                              placeholder="Middle Name"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <PPShowFormikError name="applicants.middle_name" />
                          </div>
                        </div>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${getApplicantErrorClass(
                              errors,
                              touched,

                              "last_name"
                            )}`}
                          >
                            <input
                              type="text"
                              name={`applicants.last_name`}
                              id={`applicants.last_name`}
                              value={values.applicants?.last_name}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                              placeholder="Last Name*"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <PPShowFormikError name="applicants.last_name" />
                          </div>
                        </div>
                      </div>

                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${getApplicantErrorClass(
                              errors,
                              touched,

                              "email"
                            )}`}
                          >
                            <label>Your email*</label>
                            <input
                              type="text"
                              name={`applicants.email`}
                              id={`applicants.email`}
                              value={values.applicants?.email}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                              placeholder="Please enter your email"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <PPShowFormikError name="applicants.email" />
                          </div>
                        </div>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${getApplicantErrorClass(
                              errors,
                              touched,

                              "contact_number"
                            )}`}
                          >
                            <label>Your phone*</label>
                            <input
                              type="text"
                              name={`applicants.contact_number`}
                              id={`applicants.contact_number`}
                              value={values.applicants?.contact_number}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                              placeholder="Please enter your contact number"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <PPShowFormikError name="applicants.contact_number" />
                          </div>
                        </div>
                      </div>

                      <div className={appStyles.quoteFormBlockTitle}>
                        <h4 className={appStyles.colorPrimary}>Address</h4>
                      </div>
                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${getApplicantErrorClass(
                              errors,
                              touched,

                              "address"
                            )}`}
                          >
                            <label>Street Address*</label>
                            <input
                              type="text"
                              name={`applicants.address`}
                              id={`applicants.address`}
                              value={values.applicants?.address}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                              placeholder="Please enter street address..."
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <PPShowFormikError name="applicants.address" />
                          </div>
                        </div>
                      </div>
                      <div className={appStyles.formRow}>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${getApplicantErrorClass(
                              errors,
                              touched,

                              "city"
                            )}`}
                          >
                            <label>City*</label>
                            <input
                              type="text"
                              name={`applicants.city`}
                              id={`applicants.city`}
                              value={values.applicants?.city}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                              placeholder="City"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <PPShowFormikError name="applicants.city" />
                          </div>
                        </div>
                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField} ${getApplicantErrorClass(errors, touched, "post_code")}`}
                          >
                            <label>Postal Code*</label>
                            <input
                              type="text"
                              name={`applicants.post_code`}
                              id={`applicants.post_code`}
                              value={values.applicants?.post_code}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                              placeholder="Postal Code"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <PPShowFormikError name="applicants.post_code" />
                          </div>
                        </div>

                        <div className={appStyles.formBlock}>
                          <div
                            className={`${appStyles.formField}  ${
                              getIn(touched, `applicants.country_code`) && getIn(errors, `applicants.country_code`)
                                ? appStyles.hasError
                                : ""
                            }`}
                          >
                            <label>Country*</label>

                            {hostCountries.length === 1 ? (
                              <input
                                type="text"
                                name={`applicants.country_code`}
                                id={`applicants.country_code`}
                                value={quoteData.host_country ? quoteData.host_country.name : ""}
                                className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                                disabled={true}
                              />
                            ) : (
                              <Select
                                isSearchable={true}
                                name={`applicants.country_code`}
                                id={`applicants.country_code`}
                                required={true}
                                options={hostCountries}
                                isLoading={loader === "master_data"}
                                classNames={{
                                  control: selectControlStyling,
                                  option: selectOptionControlStyling,
                                }}
                                isDisabled={true}
                                onBlur={handleBlur}
                                onChange={(newValue) => {
                                  setSelectedCountry(newValue);
                                  setFieldValue("country_code", newValue?.code);
                                }}
                                value={selectedCountry}
                                getOptionValue={(option: any) => `${option["code"]}`}
                                getOptionLabel={(option: any) => `${option["name"]}`}
                                blurInputOnSelect={false}
                              />
                            )}
                            <PPShowFormikError name="applicants.country_code" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>

                  <div className={appStyles.pageSubTitle}>
                    <h3 className={appStyles.colorPrimary}>Agreement(s)</h3>
                  </div>

                  <div className={`${appStyles.quoteFormBlock} `}>
                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            errors.accept_terms_conditions && touched.accept_terms_conditions && appStyles.hasError
                          }`}
                        >
                          <div className={`${appStyles.bfCheckBox}`}>
                            <label
                              htmlFor="accept_terms_conditions"
                              className={`${appStyles.formLabelHelper} ${appStyles.PPFormLabelHelper}`}
                            >
                              <input
                                type="checkbox"
                                id="accept_terms_conditions"
                                name="accept_terms_conditions"
                                value="yes"
                                checked={values.accept_terms_conditions}
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  setFieldValue("accept_terms_conditions", e.target.checked);
                                }}
                              />
                              <span className={`${appStyles.bfCheckmark}`}></span>I request and expressly agree that
                              this insurance policy and all pre-contractual information be provided to me in English and
                              confirm that I understand and accept the contract and agree to be bound by its terms and
                              conditions.
                            </label>
                            <PPShowFormikError name="accept_terms_conditions" msg={errors.accept_terms_conditions} />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            errors.consent_personal_data && touched.consent_personal_data && appStyles.hasError
                          }`}
                        >
                          <div className={`${appStyles.bfCheckBox}`}>
                            <label
                              htmlFor="consent_personal_data"
                              className={`${appStyles.formLabelHelper} ${appStyles.PPFormLabelHelper}`}
                            >
                              <input
                                type="checkbox"
                                id="consent_personal_data"
                                name="consent_personal_data"
                                value="yes"
                                checked={values.consent_personal_data}
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  setFieldValue("consent_personal_data", e.target.checked);
                                }}
                              />
                              <span className={`${appStyles.bfCheckmark}`}></span>I consent to the use of certain
                              special categories of personal data.
                            </label>
                            <PPShowFormikError name="consent_personal_data" msg={errors.consent_personal_data} />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            errors.on_behalf_anyone_else && touched.on_behalf_anyone_else && appStyles.hasError
                          }`}
                        >
                          <div className={`${appStyles.bfCheckBox}`}>
                            <label
                              htmlFor="on_behalf_anyone_else"
                              className={`${appStyles.formLabelHelper} ${appStyles.PPFormLabelHelper}`}
                            >
                              <input
                                type="checkbox"
                                id="on_behalf_anyone_else"
                                name="on_behalf_anyone_else"
                                value="yes"
                                checked={values.on_behalf_anyone_else}
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  setFieldValue("on_behalf_anyone_else", e.target.checked);
                                }}
                              />
                              <span className={`${appStyles.bfCheckmark}`}></span>I am not applying for this insurance
                              on behalf of anyone else.
                            </label>
                            <PPShowFormikError name="on_behalf_anyone_else" msg={errors.on_behalf_anyone_else} />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            errors.coverage_outside_residence_policy &&
                            touched.coverage_outside_residence_policy &&
                            appStyles.hasError
                          }`}
                        >
                          <div className={appStyles.bfCheckBox}>
                            <label
                              htmlFor="coverage_outside_residence_policy"
                              className={`${appStyles.formLabelHelper} ${appStyles.PPFormLabelHelper}`}
                            >
                              <input
                                type="checkbox"
                                id="coverage_outside_residence_policy"
                                name="coverage_outside_residence_policy"
                                value="yes"
                                checked={values.coverage_outside_residence_policy}
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  setFieldValue("coverage_outside_residence_policy", e.target.checked);
                                }}
                              />
                              <span className={appStyles.bfCheckmark}></span>I understand this insurance policy only
                              covers trips taken outside of my country of residence, in conjunction with the school or
                              university listed as the master policyholder in the Evidence of Cover.
                            </label>
                            <PPShowFormikError
                              name="coverage_outside_residence_policy"
                              msg={errors.coverage_outside_residence_policy}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            errors.acknowledge && touched.acknowledge && appStyles.hasError
                          }`}
                        >
                          <div className={appStyles.bfCheckBox}>
                            <label
                              htmlFor="acknowledge"
                              className={`${appStyles.formLabelHelper} ${appStyles.PPFormLabelHelper}`}
                            >
                              <input
                                type="checkbox"
                                id="acknowledge"
                                name="acknowledge"
                                value="yes"
                                checked={values.acknowledge}
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  setFieldValue("acknowledge", e.target.checked);
                                }}
                              />
                              <span className={appStyles.bfCheckmark}></span> I acknowledge that I have read through
                              <a
                                href={`${portugueseUrlAndDocs.travelMedicalDisclosures}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={appStyles.textDecorationNone}
                              >
                                <strong> insurance product information (IPID)</strong>
                              </a>
                              .
                            </label>

                            <PPShowFormikError name="acknowledge" msg={errors.acknowledge} />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={appStyles.formRow}>
                      <div className={appStyles.formBlock}>
                        <div
                          className={`${appStyles.formField} ${
                            errors.insurance_disclosure && touched.insurance_disclosure && appStyles.hasError
                          }`}
                        >
                          <div className={appStyles.bfCheckBox}>
                            <label
                              htmlFor="insurance_disclosure"
                              className={`${appStyles.formLabelHelper} ${appStyles.PPFormLabelHelper}`}
                            >
                              <input
                                type="checkbox"
                                id="insurance_disclosure"
                                name="insurance_disclosure"
                                value="yes"
                                checked={values.insurance_disclosure}
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  setFieldValue("insurance_disclosure", e.target.checked);
                                }}
                              />
                              <span className={appStyles.bfCheckmark}></span> I have read, understand and agree to the
                              <a
                                href={`${portugueseUrlAndDocs.insuranceDisclosure}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${appStyles.textDecorationNone}`}
                              >
                                <strong> Insurance disclosures</strong>
                              </a>
                              .
                            </label>

                            <PPShowFormikError name="insurance_disclosure" msg={errors.insurance_disclosure} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`${appStyles.paymentButtonWrapper} ${appStyles.formRow}`}>
                    <div className={`${appStyles.buttonBlock} ${appStyles.buttonBlockNextPrev}`}>
                      <PrevButton onClick={() => previous()} />

                      <NextButton title="Buy Now" type="submit" />
                    </div>
                  </div>

                  {hasError && <PPShowApiError />}

                  <PPFooter />

                  <PPMobileFooter title="Buy Now" />
                </div>
                <PPQuoteSummary pay={true} quoteClass={appStyles.top10} />
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default PPOrderForm;
